import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import { LogOut } from "lucide-react";

import { AppFooter } from "@/components/app-footer";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { logout, ROLE_LABELS, switchTenant } from "@/lib/api";
import { meQuery } from "@/lib/auth";

/** Frame of all pages behind the login: header with tenant, user and logout, footer with the version. */
export function AppLayout() {
  const { data: me } = useSuspenseQuery(meQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      queryClient.clear();
      queryClient.setQueryData(meQuery.queryKey, null);
      await navigate({ to: "/anmelden" });
    },
  });
  const changeTenant = useMutation({
    mutationFn: switchTenant,
    onSuccess: async (next) => {
      // Everything cached belongs to the old tenant.
      queryClient.clear();
      queryClient.setQueryData(meQuery.queryKey, next);
      await router.invalidate();
    },
  });

  // The route guard makes sure there is a session.
  if (!me) return null;

  return (
    <div className="flex min-h-svh flex-col">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-6 px-4">
          <Link to="/" className="font-semibold">
            ONIX-Katalog
          </Link>
          <nav className="flex gap-4 text-sm text-muted-foreground">
            <Link
              to="/"
              activeOptions={{ exact: true }}
              activeProps={{ className: "text-foreground" }}
            >
              Katalog
            </Link>
            {me.tenant.role !== "viewer" && (
              <Link to="/import" activeProps={{ className: "text-foreground" }}>
                Import
              </Link>
            )}
            <Link to="/export" activeProps={{ className: "text-foreground" }}>
              Export
            </Link>
            <Link to="/medien" activeProps={{ className: "text-foreground" }}>
              Medien
            </Link>
            <Link to="/auftraege" activeProps={{ className: "text-foreground" }}>
              Aufträge
            </Link>
            <Link to="/regeln" activeProps={{ className: "text-foreground" }}>
              Regeln
            </Link>
            <Link to="/mitglieder" activeProps={{ className: "text-foreground" }}>
              Mitglieder
            </Link>
            <Link to="/status" activeProps={{ className: "text-foreground" }}>
              Status
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {me.tenants.length > 1 ? (
              <Select
                aria-label="Verlag wechseln"
                value={me.tenant.id}
                disabled={changeTenant.isPending}
                onChange={(e) => changeTenant.mutate(Number(e.target.value))}
              >
                {me.tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="font-medium">{me.tenant.name}</span>
            )}
            <span className="hidden text-muted-foreground sm:inline" title={me.user.email}>
              {me.user.name || me.user.email} · {ROLE_LABELS[me.tenant.role]}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
            >
              <LogOut aria-hidden /> Abmelden
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <AppFooter />
    </div>
  );
}
